const COOKIE_NAME = '_rm_cook_ok';

const cookieOptions = {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax' as const,
    path: '/',
    secure: !import.meta.dev,
};

/**
 * Read consent cookie directly from `document.cookie` (client only).
 * On a full page reload, `useCookie()` can lag behind the browser cookie store for a tick;
 * GTM/gtag `onBefore*` hooks run in that window and must still see prior consent.
 */
export function readRmCookOkFromDocument(): string | null {
    if (!import.meta.client || typeof document === 'undefined') {
        return null;
    }
    const prefix = `${COOKIE_NAME}=`;
    for (const part of document.cookie.split('; ')) {
        if (part.startsWith(prefix)) {
            try {
                return decodeURIComponent(part.slice(prefix.length));
            } catch {
                return part.slice(prefix.length);
            }
        }
    }
    return null;
}

/**
 * Cookie consent for public site: '1' = analytics accepted, '0' = essential only.
 * Persisted for 1 year so the banner does not repeat every session.
 */
export function useCookieConsent() {
    const cookie = useCookie(COOKIE_NAME, cookieOptions);

    const hasAnswered = computed(() => {
        const v = cookie.value;
        if (v !== null && v !== undefined && v !== '') {
            return true;
        }
        const fromDoc = readRmCookOkFromDocument();
        return fromDoc !== null && fromDoc !== '';
    });

    const hasAcceptedAnalytics = computed(() => {
        const v = cookie.value;
        if (v === '1') {
            return true;
        }
        if (v === '0') {
            return false;
        }
        return readRmCookOkFromDocument() === '1';
    });

    return {
        cookie,
        hasAnswered,
        hasAcceptedAnalytics,
    };
}
